import { create } from 'zustand';
import { api } from './api';

export interface SessionUser {
  userId: string;
  email: string;
  name: string;
  avatarUrl: string;
  isSuperadmin: boolean;
}

interface SessionResponse {
  user: SessionUser;
  impersonator: { userId: string; name: string; email: string } | null;
  token: string;
  expiresAt: number;
  spacetime: { uri: string; database: string };
}

interface SessionState {
  status: 'loading' | 'signed-in' | 'signed-out';
  user: SessionUser | null;
  impersonator: SessionResponse['impersonator'];
  token: string;
  expiresAt: number;
  spacetime: SessionResponse['spacetime'] | null;
  load(): Promise<void>;
  refreshToken(): Promise<string>;
  logout(): Promise<void>;
}

export const useSession = create<SessionState>((set, get) => ({
  status: 'loading',
  user: null,
  impersonator: null,
  token: '',
  expiresAt: 0,
  spacetime: null,
  async load() {
    try {
      const s = await api<SessionResponse>('/auth/session');
      set({ status: 'signed-in', user: s.user, impersonator: s.impersonator, token: s.token, expiresAt: s.expiresAt, spacetime: s.spacetime });
    } catch {
      set({ status: 'signed-out', user: null, token: '' });
    }
  },
  async refreshToken() {
    const s = await api<SessionResponse>('/auth/session');
    set({ user: s.user, impersonator: s.impersonator, token: s.token, expiresAt: s.expiresAt });
    return s.token;
  },
  async logout() {
    await api('/auth/logout', { method: 'POST' }).catch(() => {});
    set({ status: 'signed-out', user: null, token: '' });
    window.location.href = '/login';
  },
}));

// Keep a fresh access token around for API calls that need it.
setInterval(() => {
  const s = useSession.getState();
  if (s.status === 'signed-in' && s.expiresAt - Date.now() < 3 * 60_000) void s.refreshToken().catch(() => s.load());
}, 30_000);
